// react and js
import * as Yup from "yup";
import { React, useContext, useState, useRef, useEffect } from "react";
import {
  Link as RouterLink,
  useSearchParams,
  useNavigate,
} from "react-router-dom";

import { useFormik, Form, FormikProvider } from "formik";
import { useTranslation } from "react-i18next";

// mui
import {
  Link,
  IconButton,
  InputAdornment,
  Collapse,
  Card,
  Box,
  Typography,
  Grid,
} from "@mui/material";

// components
import Iconify from "../../components/Iconify";
import backendRequest from "../../components/BackendRequest";

// hooks
import appContext from "../../hooks/appContext";

// layouts

// pages

// theme
import {
  AuthenticationButton,
  AuthenticationTextField,
  AuthenticationAlertError,
  AuthenticationAlertSuccess,
} from "../../theme/styled";

// utils
import { TranslatedMessage } from "../../utils/common";

// widgets

// app

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { stateStorage } = useContext(appContext);
  const myLocalStateStorage = JSON.parse(
    localStorage.getItem("myLocalStateStorage")
  );

  const wrapperRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const [timeNoDataCheck, setTimeNoDataCheck] = useState(0);

  const handleClick = () => {
    setError(false);
    setSuccess(false);
  };
  document.addEventListener("mousedown", handleClick, {
    passive: false,
    capture: false,
  });

  const NewPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required(t("newPassword.errors.passwordRequired"))
      .min(8, t("newPassword.errors.passwordMin"))
      .max(64, t("newPassword.errors.passwordMax"))
      .matches(/[0-9]/, t("newPassword.errors.passwordNumber"))
      .matches(/[a-z]/, t("newPassword.errors.passwordLowercase"))
      .matches(/[A-Z]/, t("newPassword.errors.passwordUppercase")),
    // .matches(/[^\w]/, 'Password requires a symbol')
    passwordRepeat: Yup.string()
      .required(t("newPassword.errors.passwordRepeatRequired"))
      .oneOf([Yup.ref("password"), null], t("newPassword.errors.passwordsMustMatch")),
  });

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const [ModeChecked] = useState(
    searchParams.get("5c88da93a09cd871e902ea3daf8927d6")
  );
  const [DashboardIdChecked] = useState(
    searchParams.get("a96194a965bec98ffc3aeba420ff8e2f")
  );
  const [DashboardPasswordResetTokenChecked] = useState(
    searchParams.get("ccc8c5d2386123252eaaae2b47f6738f")
  );

  useEffect(() => {
    const timerID = setTimeout(() => {
      setTimeNoDataCheck(timeNoDataCheck + 1);
    }, 50);

    return () => {
      clearTimeout(timerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeNoDataCheck]);

  useEffect(() => {
    if (ModeChecked === null) {
      navigate("/");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeNoDataCheck]);

  const handleNewPassword = (values, setSubmitting) => {
    if (navigator.onLine === true) {
      setError(false);
      setSuccess(false);

      if (ModeChecked !== undefined && ModeChecked !== null) {
        const sendData = {
          request_type: "dashboard",
          route_info: "authentication",
          client: {
            mode: "new_password",
            password: values.password,
            dashboard_id: DashboardIdChecked,
            dashboard_password_reset_token: DashboardPasswordResetTokenChecked,
          },
        };

        backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
          .then((contentFromBackend) => {
            const keyOnly = Object.keys(contentFromBackend);
            const checkKey = keyOnly.includes("answer");

            if (checkKey === true) {
              if (contentFromBackend.answer.message === "success") {
                setReturnMessage(<TranslatedMessage tKey="newPassword.status.success" />);
                setSuccess(true);
                setError(false);
              } else {
                setReturnMessage(<TranslatedMessage tKey="newPassword.status.invalidRequest" />);
                setError(true);
              }
            } else {
              setReturnMessage(<TranslatedMessage tKey="newPassword.status.invalidRequest" />);
              setError(true);
            }
            setSubmitting(false);
          })
          .catch(() => {
            if (navigator.onLine === true) {
              setReturnMessage(<TranslatedMessage tKey="newPassword.status.unknownError" />);
              setError(true);
              setSubmitting(false);
            }
          });
      } else {
        setReturnMessage(<TranslatedMessage tKey="newPassword.status.invalidRequest" />);
        setError(true);
        setSubmitting(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      passwordRepeat: "",
    },
    validationSchema: NewPasswordSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleNewPassword(values, setSubmitting);
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        direction="column"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 800,
          px: 2,
        }}
      >
        <Card
          sx={{
            background: "#F2F2F2",
            borderColor: "#6D6E70",
            boxShadow: "0px 0px 5px 0px rgb(255, 255, 255)",
          }}
        >
          <Box sx={{ p: 4 }} dir="ltr">
            <Typography
              variant="h2"
              sx={{
                mt: 0,
                mb: 0,
              }}
            >
              {t("newPassword.title")}
            </Typography>

            <Box sx={{ mt: 1, mb: 1 }} dir="ltr">
              <Typography
                variant="contentMiddle"
                sx={{ mt: 1, textAlign: "center" }}
              >
                {t("newPassword.subtitle")}
              </Typography>
            </Box>

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(touched.password && errors.password) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.password && errors.password) === true
                    ? errors.password
                    : t("newPassword.labels.newPassword")}
                </Typography>

                <AuthenticationTextField
                  autoComplete="new-password" // Avoids chrome warning
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  {...getFieldProps("password")}
                  error={Boolean(touched.password && errors.password)}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify
                            icon={
                              showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  variant="contentSmallBold"
                  sx={{
                    color:
                      Boolean(
                        touched.passwordRepeat && errors.passwordRepeat
                      ) === true
                        ? "red"
                        : "none",
                  }}
                >
                  {Boolean(touched.passwordRepeat && errors.passwordRepeat) ===
                  true
                    ? errors.passwordRepeat
                    : t("newPassword.labels.repeatNewPassword")}
                </Typography>

                <AuthenticationTextField
                  autoComplete="new-password" // Avoids chrome warning
                  fullWidth
                  hiddenLabel
                  variant="outlined"
                  size="small"
                  type={showPassword ? "text" : "password"}
                  {...getFieldProps("passwordRepeat")}
                  error={Boolean(
                    touched.passwordRepeat && errors.passwordRepeat
                  )}
                  sx={{ mt: "4px", mb: 1 }}
                  InputLabelProps={{ shrink: false }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Iconify
                            icon={
                              showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Collapse
                  ref={wrapperRef}
                  in={error}
                  timeout="auto"
                  unmountOnExit
                >
                  <AuthenticationAlertError
                    severity="error"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    {returnMessage}
                  </AuthenticationAlertError>
                </Collapse>

                <Collapse
                  ref={wrapperRef}
                  in={success}
                  timeout="auto"
                  unmountOnExit
                >
                  <AuthenticationAlertSuccess
                    severity="success"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    {returnMessage}
                  </AuthenticationAlertSuccess>
                </Collapse>

                <Box sx={{ textAlign: "center", mt: 1, mb: 1 }}>
                  {!(
                    stateStorage.authenticated === true &&
                    myLocalStateStorage !== null
                  ) ? (
                    <Typography variant="contentSmall">
                      <Link
                        component={RouterLink}
                        to="/login"
                        underline="none"
                        variant="contentSmallLink"
                        color="inherit"
                      >
                        {t("newPassword.links.backToLogin")}
                      </Link>
                    </Typography>
                  ) : null}

                  {stateStorage.authenticated === true &&
                  myLocalStateStorage !== null ? (
                    <Typography variant="contentSmall">
                      <Link
                        component={RouterLink}
                        to="/"
                        underline="none"
                        variant="contentSmallLink"
                        color="inherit"
                      >
                        {t("newPassword.links.backToDashboard")}
                      </Link>
                    </Typography>
                  ) : null}
                </Box>

                <AuthenticationButton
                  fullWidth
                  variant="contained"
                  loading={isSubmitting}
                  type="submit"
                  sx={{ mt: 1 }}
                >
                  {t("newPassword.submitButton")}
                </AuthenticationButton>
              </Form>
            </FormikProvider>
          </Box>
        </Card>
      </Box>
    </Grid>
  );
}
